/**
 * @example
 * ```ts
 * type DiscriminatedObjects = { type: 'a', a: 1 } | { type: 'b', b: 2 };
 * type DiscriminatingKey = 'type';
 *
 * type Result = DiscriminatedToMap<DiscriminatedObjects, DiscriminatingKey>;
 * // Result = { a?: { type: 'a', a: 1 } | undefined, b?: { type: 'b', b: 2 } | undefined }
 * ```
 */
type DiscriminatedToMap<
  Discriminated extends Record<string, any>,
  Key extends keyof Discriminated
> = {
  [K in Discriminated[Key]]?: Discriminated & {
    [KK in Key]-?: K;
  };
};

/**
 * @example
 * ```ts
 * const arr = [
 *  { type: 'a', a: 1 },
 *  { type: 'b', b: 2 },
 *  { type: 'c', c: 3 },
 * ];
 *
 * const map = discriminatedArrayToMap(arr, 'type');
 * // map = { a: { type: 'a', a: 1 }, b: { type: 'b', b: 2 }, c: { type: 'c', c: 3 } }
 * ```
 */
export function discriminatedArrayToMap<
  Arr extends Record<string, any>[],
  Key extends keyof Arr[number]
>(arr: Arr, key: Key) {
  return arr.reduce<DiscriminatedToMap<Arr[number], Key>>((map, item) => {
    if (key in item) {
      // eslint-disable-next-line no-param-reassign
      map[item[key as keyof typeof item]] = item;
    }
    return map;
  }, {} as DiscriminatedToMap<Arr[number], Key>);
}

/**
 * @example
 * ```ts
 * const arr = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
 * const result = chunkArray(arr, 3);
 * // result = [[1, 2, 3, 4], [5, 6, 7], [8, 9, 10]]
 */
export function chunkArray<T>(arr: T[], maxNum: number): T[][] {
  const result: T[][] = [];
  let chunkCount = 0;
  let start = 0;
  const chunkSize = Math.floor(arr.length / maxNum);
  const remainder = arr.length % maxNum;

  while (start < arr.length && chunkCount < maxNum) {
    const shouldIncreaseChunkSize = chunkCount >= maxNum - remainder;
    const end = start + chunkSize + (shouldIncreaseChunkSize ? 1 : 0);
    result.push(arr.slice(start, end));
    start = end;
    chunkCount += 1;
  }

  return result;
}
